/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Edit2, Trash2, Check, MessageSquare } from 'lucide-react';
import { serviceRequestService } from '../../services/serviceRequestService';
import toastService from '../../utils/toastService';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { SERVICE_REQUEST_CHECKLIST_COMMENT_MAX_LENGTH } from '../../constants/serviceRequests';
import ChecklistDeleteModal from './ChecklistDeleteModal';
import ChecklistIconTooltip from './ChecklistIconTooltip';
import {
  apiErrorMessage,
  canEditChecklistComment,
  checklistPermissionsShape,
  initialsOf,
  formatDateTime,
} from './checklistHelpers';

const COMMENTS_PAGE_SIZE = 20;

// The API pages newest first; the thread shows oldest first. Older pages are
// prepended, skipping comments already shown (adds shift the page offsets).
const mergeOlder = (older, current) => {
  const shown = new Set(current.map((c) => c.uid));
  return [...older.filter((c) => !shown.has(c.uid)), ...current];
};

/** Comment thread of one checklist item. Mounted only while the item is expanded. */
const ChecklistItemComments = ({
  serviceRequestUid,
  checklistUid,
  itemUid,
  permissions,
  onCountChange,
  onChanged,
}) => {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingUid, setEditingUid] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await serviceRequestService.getChecklistItemComments(
        serviceRequestUid, checklistUid, itemUid, { page: 1, limit: COMMENTS_PAGE_SIZE },
      );
      setComments([...(data?.data || [])].reverse());
      setTotal(data?.total || 0);
      setPage(1);
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to load comments'));
    } finally {
      setLoading(false);
    }
  }, [serviceRequestUid, checklistUid, itemUid]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const loadOlder = async () => {
    const nextPage = page + 1;
    try {
      setLoadingOlder(true);
      const { data } = await serviceRequestService.getChecklistItemComments(
        serviceRequestUid, checklistUid, itemUid, { page: nextPage, limit: COMMENTS_PAGE_SIZE },
      );
      setComments((prev) => mergeOlder([...(data?.data || [])].reverse(), prev));
      setTotal(data?.total || 0);
      setPage(nextPage);
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to load comments'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const olderCount = Math.max(0, total - comments.length);

  const handleAdd = async (e) => {
    e.preventDefault();
    const comment = draft.trim();
    if (!comment) return;
    try {
      setAdding(true);
      const { data } = await serviceRequestService.addChecklistItemComment(serviceRequestUid, checklistUid, itemUid, comment);
      setComments((prev) => [...prev, data]);
      setTotal((prev) => prev + 1);
      setDraft('');
      onCountChange(1);
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to add comment'));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (comment) => {
    setEditingUid(comment.uid);
    setEditDraft(comment.comment);
  };

  const cancelEdit = () => {
    setEditingUid(null);
    setEditDraft('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const comment = editDraft.trim();
    if (!comment || !editingUid) return;
    try {
      setSaving(true);
      const { data } = await serviceRequestService.updateChecklistItemComment(
        serviceRequestUid, checklistUid, itemUid, editingUid, comment,
      );
      setComments((prev) => prev.map((c) => (c.uid === data.uid ? data : c)));
      cancelEdit();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to update comment'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await serviceRequestService.deleteChecklistItemComment(serviceRequestUid, checklistUid, itemUid, deleteTarget.uid);
      setComments((prev) => prev.filter((c) => c.uid !== deleteTarget.uid));
      setTotal((prev) => Math.max(0, prev - 1));
      setDeleteTarget(null);
      onCountChange(-1);
      toastService.success('Comment deleted successfully');
      onChanged();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to delete comment'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-2 ml-8 pl-3 border-l-2 border-base-content/10 space-y-2">
      {!loading && olderCount > 0 && (
        <button
          type="button"
          onClick={loadOlder}
          disabled={loadingOlder}
          className="btn btn-xs btn-ghost rounded-lg gap-1 font-bold text-primary"
        >
          {loadingOlder && <span className="loading loading-spinner loading-xs"></span>}
          Show older comments ({olderCount} more)
        </button>
      )}
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 bg-base-200/60 rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : total === 0 ? (
        <p className="text-xs text-base-content/40 py-1">No comments yet.</p>
      ) : (
        comments.map((comment) => {
          const authorName = comment.createdByName || 'Unknown user';
          const editorName = comment.updatedByName || 'Unknown user';
          const isEditing = editingUid === comment.uid;
          const canEdit = canEditChecklistComment(permissions, comment);
          const canDelete = !!permissions.commentDelete;

          return (
            <div key={comment.uid} className="bg-base-200/40 hover:bg-base-200/70 p-2.5 rounded-xl border border-base-content/5 transition-colors">
              {isEditing ? (
                <form onSubmit={handleSave}>
                  <textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    maxLength={SERVICE_REQUEST_CHECKLIST_COMMENT_MAX_LENGTH}
                    rows={2}
                    autoFocus
                    aria-label="Edit comment"
                    className="textarea textarea-bordered w-full rounded-xl text-sm leading-relaxed bg-base-100 focus:outline-none focus:border-primary resize-none"
                    disabled={saving}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button type="button" onClick={cancelEdit} className="btn btn-xs btn-ghost rounded-lg font-bold" disabled={saving}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-xs btn-primary rounded-lg gap-1 font-bold"
                      disabled={saving || !editDraft.trim() || editDraft.trim() === comment.comment}
                    >
                      {saving ? <span className="loading loading-spinner loading-xs"></span> : <Check size={12} />}
                      Save
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-sm text-base-content leading-relaxed whitespace-pre-wrap break-words">
                  {comment.comment}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1.5 text-[11px] text-base-content/50 font-medium flex-wrap">
                <span className="inline-flex items-center gap-1 font-bold text-base-content/70">
                  <span className="w-4 h-4 rounded-full bg-base-content/10 text-[9px] flex items-center justify-center font-black">
                    {initialsOf(authorName)}
                  </span>
                  {authorName}
                </span>
                <span>·</span>
                <span title={formatDateTime(comment.createdAt)}>{formatRelativeTime(comment.createdAt)}</span>
                {comment.updatedAt && (
                  <>
                    <span>·</span>
                    <span className="italic" title={`Edited by ${editorName} on ${formatDateTime(comment.updatedAt)}`}>
                      edited
                    </span>
                  </>
                )}
                {!isEditing && (canEdit || canDelete) && (
                  <span className="ml-auto inline-flex items-center gap-0.5">
                    {canEdit && (
                      <ChecklistIconTooltip tip="Edit">
                        <button
                          type="button"
                          onClick={() => startEdit(comment)}
                          aria-label="Edit comment"
                          className="p-1 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                        >
                          <Edit2 size={12} />
                        </button>
                      </ChecklistIconTooltip>
                    )}
                    {canDelete && (
                      <ChecklistIconTooltip tip="Delete" variant="error" position="left">
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(comment)}
                          aria-label="Delete comment"
                          className="p-1 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      </ChecklistIconTooltip>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}

      {permissions.commentAdd && (
        <form onSubmit={handleAdd} className="pt-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={SERVICE_REQUEST_CHECKLIST_COMMENT_MAX_LENGTH}
            rows={2}
            placeholder="Add a comment..."
            aria-label="Add a comment"
            className="textarea textarea-bordered w-full rounded-xl text-sm leading-relaxed bg-base-200/40 focus:outline-none focus:border-primary resize-none"
            disabled={adding}
          />
          <div className="flex items-center justify-between gap-3 mt-1.5">
            <span className="text-[11px] text-base-content/40 font-medium">
              {draft.length}/{SERVICE_REQUEST_CHECKLIST_COMMENT_MAX_LENGTH}
            </span>
            <button
              type="submit"
              className="btn btn-xs btn-primary rounded-lg gap-1 font-bold"
              disabled={adding || !draft.trim()}
            >
              {adding ? <span className="loading loading-spinner loading-xs"></span> : <MessageSquare size={12} />}
              Comment
            </button>
          </div>
        </form>
      )}

      <ChecklistDeleteModal
        isOpen={!!deleteTarget}
        title="Delete Comment"
        message="Are you sure you want to delete this comment?"
        preview={deleteTarget?.comment}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

ChecklistItemComments.propTypes = {
  serviceRequestUid: PropTypes.string.isRequired,
  checklistUid: PropTypes.string.isRequired,
  itemUid: PropTypes.string.isRequired,
  permissions: checklistPermissionsShape.isRequired,
  onCountChange: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default ChecklistItemComments;
